import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "flex flex-wrap items-start gap-5" }
const _hoisted_2 = { class: "flex items-center gap-2 h-7 mb-[5px]" }
const _hoisted_3 = {
  key: 0,
  class: "mt-2"
}
const _hoisted_4 = {
  key: 0,
  class: "flex items-center gap-3"
}
const _hoisted_5 = { class: "min-w-[240px] involving-filter-wrap" }

import { onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import LlTextField from '@/components/LlTextField.vue';
import LlCheckbox from '@/components/LlCheckbox.vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import LlSelect from '@/components/LlSelect.vue';
import LlStatusBadge from '@/components/LlStatusBadge.vue';
import MatterInvolvingFilter from '@/components/MatterInvolvingFilter.vue';
import CustomFieldsFilter from '@/components/CustomFieldsFilter.vue';
import ClientsFilter from './ClientsFilter.vue';
import { useMeStore } from '@/services/UserService/useMeStore';
import { useAccountsStore } from '@/services/UserService/useAccountsStore';
import { useCustomFieldsStore } from '@/modules/settings/company/CustomFields/useCustomFieldsStore';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import { filterableCustomFields } from '@/utils/customFieldFilterHelpers';
import type { CustomFieldFilterSelections, FilterableCustomField } from '@/models/customFieldFilterTypes';
import type { MattersFilters } from './useMattersFilters';


export default /*@__PURE__*/_defineComponent({
  __name: 'MattersFilters',
  props: {
  filters: { type: Object, required: true },
  loading: { type: Boolean, default: false },
  activeFilterCount: { type: Number, default: 0 },
},
  emits: ['apply-filters', 'clear-filters'],
  setup(__props, { emit: __emit }) {

const props = __props;

const emit = __emit;

// Visible by default so nothing changes for anyone who never touches the
// toggle - matches Time Entries' same default.
const showFilters = ref(true);

const handleClearAll = () => {
  emit('clear-filters');
};

const meStore = useMeStore();
const { me } = storeToRefs(meStore);
const accountsStore = useAccountsStore();
const { accountsList } = storeToRefs(accountsStore);

const qbConnectionOptions = [
  { title: 'QuickBooks Connection: Any', value: 'any' },
  { title: 'QuickBooks Connection: Good', value: 'good' },
  { title: 'QuickBooks Connection: Bad', value: 'bad' },
  { title: 'QuickBooks Connection: Not connected', value: 'none' },
];

const applyFilters = () => {
  props.filters.page = 1;
  emit('apply-filters');
};

// 300ms (Fixed Fees/Time Entries' debounce) fired mid-word for normal typing
// pace; 1000ms felt sluggish. 500ms is the middle ground.
let searchTimeout: ReturnType<typeof setTimeout>;
const handleSearch = () => {
  clearTimeout(searchTimeout);
  searchTimeout = setTimeout(applyFilters, 500);
};

const { handleError } = useSnackbarHandler();
const customFieldsStore = useCustomFieldsStore();
const customFields = ref<FilterableCustomField[]>([]);

const loadCustomFields = async () => {
  try {
    customFields.value = filterableCustomFields(await customFieldsStore.listCustomFields(), 'matter');
  } catch (error) {
    handleError(error);
  }
};

const handleCustomFieldsChange = (selections: CustomFieldFilterSelections) => {
  props.filters.matterCustomFields = selections;
  applyFilters();
};

const handleMatterInvolvingChange = (
  updatedForm: Pick<MattersFilters, 'scopeAccount' | 'originating' | 'primary' | 'reviewer'>,
) => {
  props.filters.scopeAccount = updatedForm.scopeAccount;
  props.filters.originating = updatedForm.originating || false;
  props.filters.primary = updatedForm.primary || false;
  props.filters.reviewer = updatedForm.reviewer || false;
  props.filters.involved = props.filters.scopeAccount
    ? !(props.filters.originating || props.filters.primary || props.filters.reviewer)
    : false;
  applyFilters();
};

// Matches matters-next: Operators/Accountants see everything by default
// (empty scope); every other role defaults to their own matters where
// they're the responsible attorney.
onMounted(async () => {
  if (!me.value?.id) {
    await meStore.getMe();
  }
  if (me.value?.features?.customFieldFilters) {
    loadCustomFields();
  }
  if (!accountsList.value?.length) {
    await accountsStore.listAccounts();
  }

  const role = me.value?.role;
  if (!role || role === 'operator' || role === 'accountant') return;

  const currentUserAccount = accountsList.value?.find((acc) => acc.id === me.value.id);
  if (!currentUserAccount) return;

  props.filters.scopeAccount = currentUserAccount;
  props.filters.primary = true;
  props.filters.involved = false;
  applyFilters();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["filters-wrap rounded-lg p-3 border", showFilters.value ? 'bg-green-50 border-green-200' : 'border-transparent'])
  }, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", {
        class: _normalizeClass(["flex flex-col", { 'min-w-[300px]': showFilters.value }])
      }, [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(LlSecondaryButton, {
            variant: "outlined",
            size: "small",
            class: "text-capitalize flex-shrink-0 !h-7",
            "prepend-icon": "mdi-filter-variant",
            "append-icon": showFilters.value ? 'mdi-chevron-up' : 'mdi-chevron-down',
            "aria-label": "Toggle filters",
            "data-cy": "matters-filters-toggle",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (showFilters.value = !showFilters.value))
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(showFilters.value ? '' : 'Filters'), 1)
            ]),
            _: 1
          }, 8, ["append-icon"]),
          (showFilters.value && __props.activeFilterCount > 0)
            ? (_openBlock(), _createBlock(LlSecondaryButton, {
                key: 0,
                variant: "text",
                size: "small",
                color: "error",
                "prepend-icon": "mdi-close",
                class: "!h-7",
                "data-cy": "matters-filters-clear-all-quiet",
                onClick: handleClearAll
              }, {
                default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                  _createTextVNode(" Clear All ", -1)
                ]))]),
                _: 1
              }))
            : _createCommentVNode("", true)
        ]),
        (showFilters.value)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              _createVNode(LlTextField, {
                modelValue: __props.filters.search,
                "onUpdate:modelValue": [
                  _cache[1] || (_cache[1] = ($event: any) => ((__props.filters.search) = $event)),
                  handleSearch
                ],
                placeholder: "Search Matters",
                "prepend-inner-icon": "mdi-magnify",
                "hide-details": ""
              }, null, 8, ["modelValue"]),
              (__props.filters.checkQuickbooks)
                ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                    _createVNode(LlSelect, {
                      modelValue: __props.filters.qbConnectionType,
                      "onUpdate:modelValue": [
                        _cache[2] || (_cache[2] = ($event: any) => ((__props.filters.qbConnectionType) = $event)),
                        applyFilters
                      ],
                      items: qbConnectionOptions,
                      "item-title": "title",
                      "item-value": "value",
                      "return-object": false,
                      density: "compact",
                      variant: "outlined",
                      "hide-details": "",
                      loading: __props.loading
                    }, null, 8, ["modelValue", "loading"])
                  ]))
                : _createCommentVNode("", true),
              _createVNode(LlCheckbox, {
                modelValue: __props.filters.archived,
                "onUpdate:modelValue": [
                  _cache[3] || (_cache[3] = ($event: any) => ((__props.filters.archived) = $event)),
                  applyFilters
                ],
                label: "Show Archived",
                density: "compact",
                class: "mt-2"
              }, null, 8, ["modelValue"])
            ], 64))
          : _createCommentVNode("", true)
      ], 2),
      (!showFilters.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
            (__props.activeFilterCount > 0)
              ? (_openBlock(), _createBlock(LlStatusBadge, { key: 0 }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(__props.activeFilterCount) + " " + _toDisplayString(__props.activeFilterCount === 1 ? 'filter is' : 'filters are') + " active ", 1)
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true),
            (__props.activeFilterCount > 0)
              ? (_openBlock(), _createBlock(LlSecondaryButton, {
                  key: 1,
                  variant: "text",
                  size: "small",
                  color: "error",
                  "prepend-icon": "mdi-close",
                  "data-cy": "matters-filters-clear-all",
                  onClick: handleClearAll
                }, {
                  default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
                    _createTextVNode(" Clear All ", -1)
                  ]))]),
                  _: 1
                }))
              : _createCommentVNode("", true)
          ]))
        : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
            _createElementVNode("div", _hoisted_5, [
              _createVNode(MatterInvolvingFilter, {
                form: __props.filters,
                horizontal: true,
                onMatterInvolvingChange: handleMatterInvolvingChange
              }, null, 8, ["form"])
            ]),
            _createVNode(ClientsFilter, {
              filters: __props.filters,
              onApplyFilters: applyFilters
            }, null, 8, ["filters"]),
            _createVNode(CustomFieldsFilter, {
              fields: customFields.value,
              "model-value": __props.filters.matterCustomFields,
              "onUpdate:modelValue": handleCustomFieldsChange
            }, null, 8, ["fields", "model-value"])
          ], 64))
    ])
  ], 2))
}
}

})